import { cn } from "@/lib/utils";

import { MuiReportProblemOutlinedIcon } from "./mui-alert-icons";
import { MuiTooltip } from "./mui-tooltip";

// The donor's `EffectiveDateAlert` (`src/components/EffectiveDateAlert/
// EffectiveDateAlert.tsx`): a version's effective date on the details page's
// metadata line, in two faces, both under the same bottom-placed, arrowless
// `react-ui` tooltip whose title is the caller's ("Effective Date").
//
//   - **The latest face** — the last-published version: the text alone, a
//     body1 paragraph at `px 0.2` (1.6px).
//   - **The alert face** — any other version: the same paragraph inside MUI's
//     standard warning `Alert`, restyled (measured on the running donor, #694):
//     an inline-flex box around a flex alert, centred, `margin-inline .5rem`,
//     **no padding at all** — the styled rule's `'.MuiAlert-message, '`
//     selector ends in a comma, so its `padding: 0` lands on the alert root as
//     well as on the message — the icon at its 22px and 0.9 opacity with
//     `padding: 0 0 0 .4rem` and no right margin, in CSS `orange`; the ground
//     and ink are the standard warning's (`lighten`/`darken` of `#ffb74d`), and
//     the paragraph's own 16px / 24px type sits inside the alert's body2.
//
// The text itself is the caller's: `Effective <date>`, and on the alert face
// the range to the last-published version's date (`effectiveDateText`).

interface EffectiveDateAlertProps {
  /** The visible text — `Effective 9/1/26 - 9/11/26`. */
  text: string;
  /** The tooltip's title — the donor passes "Effective Date". */
  title: string;
  /** The last-published version draws the text alone; any other, the alert. */
  isLastPublishedVersion: boolean;
}

export function EffectiveDateAlert({
  text,
  title,
  isLastPublishedVersion,
}: EffectiveDateAlertProps) {
  const body = (
    <MuiTooltip title={title} side="bottom" asChild>
      <span>
        <p className="m-0 px-[1.6px] text-base leading-6">{text}</p>
      </span>
    </MuiTooltip>
  );

  if (isLastPublishedVersion) return <span data-testid="effective-date">{body}</span>;

  return (
    <div data-testid="effective-date-alert" className="inline-flex p-0">
      <div
        data-slot="effective-date-alert"
        role="alert"
        className={cn(
          "mx-2 flex items-center rounded-[4px] p-0 text-sm leading-[1.43]",
          "bg-mui-warning-surface text-mui-warning-ink",
        )}
      >
        <span
          aria-hidden="true"
          className="text-mui-alert-orange flex py-0 pr-0 pl-[0.4rem] text-[22px] opacity-90"
        >
          <MuiReportProblemOutlinedIcon />
        </span>
        <div className="min-w-0 overflow-auto p-0">{body}</div>
      </div>
    </div>
  );
}
